<template>
  <div class="" v-loader.opacity.tab="loading">
    <div class="title-box">
      <h2 class="title">{{ form.uuid ? $t('integration.edit') : $t('integration.new') }}</h2>
    </div>

    <div class="alert" :class="alert.class" role="alert" v-if="alert.message">
      <span v-html="alert.message"></span>
      <button type="button" class="btn-close" aria-label="Close" @click="alert.message = null"></button>
    </div>

    <div class="clinic-integration-box scrollbar">
      <div class="row">
        <div class="col-lg-6">
          <div class="form-floating mb-3">
            <input type="text" class="form-control" ref="name" id="name" v-model="form.name" :placeholder="$t('integration.name')" data-test="inputIntegrationName">
            <label for="name">{{ $t('integration.name') }}*</label>
          </div>
        </div>
        <div class="col-lg-3">
          <div class="form-floating v-select-floating mb-3">
            <v-select
              class="v-select form"
              id="typeSelected"
              :placeholder="$t('integration.type')"
              v-model="form.type"
              data-test="vSelectIntegrationType"
              :reduce="x => x.value"
              :clearable="false"
              :options="typeOptions">
            </v-select>
            <label for="typeSelected">{{ $t('integration.type') }}</label>
          </div>
        </div>
        <div class="col-lg-3">
          <div class="form-floating">
            <div class="form-check form-switch">
              <label class="form-check-label" for="integrationEnabled">{{ $t('integration.enabled') }}</label>
              <input class="form-check-input"
                     type="checkbox"
                     role="switch"
                     id="integrationEnabled"
                     v-model="form.enabled"
                     data-test="checkIntegrationEnabled">
            </div>
          </div>
        </div>
      </div>

      <template v-if="form.type === 'sftp'">
        <section>
          <h3 class="sub-title">{{ $t('integration.sftpSettings') }}</h3>

          <div class="row">
            <div class="col-lg-8">
              <div class="form-floating">
                <input type="text" class="form-control" id="sftpHost" v-model="form.config.host" :placeholder="$t('integration.host')" data-test="inputSftpHost">
                <label for="sftpHost">{{ $t('integration.host') }}</label>
              </div>
            </div>
            <div class="col-lg-4">
              <div class="form-floating">
                <input type="number" class="form-control" id="sftpPort" v-model.number="form.config.port" :placeholder="$t('integration.port')" data-test="inputSftpPort">
                <label for="sftpPort">{{ $t('integration.port') }}</label>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-lg-6">
              <div class="form-floating">
                <input type="text" class="form-control" id="sftpUsername" v-model="form.config.username" :placeholder="$t('integration.username')" autocomplete="off" data-test="inputSftpUsername">
                <label for="sftpUsername">{{ $t('integration.username') }}</label>
              </div>
            </div>
            <div class="col-lg-6">
              <div class="form-floating">
                <input type="password" class="form-control" id="sftpPassword" v-model="form.config.password" :placeholder="$t('integration.password')" autocomplete="new-password" data-test="inputSftpPassword" @focus="clearMaskedPassword">
                <label for="sftpPassword">{{ $t('integration.password') }}</label>
              </div>
            </div>
          </div>

          <div class="form-floating">
            <input type="text" class="form-control" id="sftpRemoteDir" v-model="form.config.remoteDir" :placeholder="$t('integration.remoteDir')" data-test="inputSftpRemoteDir">
            <label for="sftpRemoteDir">{{ $t('integration.remoteDir') }}</label>
          </div>

          <div class="form-floating">
            <textarea class="form-control known-hosts" id="sftpKnownHosts" v-model="form.config.knownHosts" :placeholder="$t('integration.knownHosts')" spellcheck="false" data-test="inputSftpKnownHosts"></textarea>
            <label for="sftpKnownHosts">{{ $t('integration.knownHosts') }}</label>
          </div>
          <p class="field-hint">{{ $t('integration.knownHostsHint') }}</p>
        </section>
      </template>
    </div>

    <div class="action-bar">
      <button class="btn btn-success" type="button" name="button" data-test="btnSave" @click="doSave">
        <span>{{ $t('save') }}</span>
      </button>
      <button class="btn btn-secondary" type="button" name="button" data-test="btnCancel" @click="$emit('cancel-edit')">
        <span>{{ $t('cancel') }}</span>
      </button>
      <button v-if="form.type === 'sftp'" class="btn btn-outline-primary" type="button" name="button" data-test="btnTest" :disabled="!form.config.host || !form.config.username" @click="doTest">
        <i class="fa fa-plug"></i>
        <span>{{ $t('integration.test') }}</span>
      </button>
      <button v-if="form.uuid" class="btn btn-outline-danger" type="button" name="button" data-test="btnRemove" @click="confirmRemoval">
        <i class="fa fa-ban"></i>
        <span>{{ $t('remove') }}</span>
      </button>
    </div>

    <BaseGenericOffcanvas :offcanvas_id="'offcanvasClinicIntegrationGeneric'" ref="offcanvasGeneric"></BaseGenericOffcanvas>
  </div>
</template>

<style lang="scss" scoped>
.clinic-integration-box {
  height:                       calc(100dvh - 394px);
  padding:                      10px;
  overflow-x:                   hidden;
  overflow-y:                   scroll;
}

textarea.known-hosts {
  height:                       110px;
  min-height:                   110px;
  font-family:                  monospace;
  font-size:                    12px;
  white-space:                  pre;
  overflow-wrap:                normal;
  overflow-x:                   auto;
}

.field-hint {
  margin-bottom:                1rem;
  font-size:                    12px;
  opacity:                      0.7;
}

section {
  display:                      flex;
  flex-direction:               column;
  gap:                          19px;
}

@media (max-width: 991px) {
  .clinic-integration-box {
    height:                     calc(100dvh - 309px);
    overflow-x:                 hidden;
    overflow-y:                 scroll;
  }

  section {
    .sub-title,
    .row .col-lg-4,
    .row .col-lg-6:last-child {
      margin-top:               19px;
    }
  }
}

@media screen and (min-width: 992px) and (max-width: 1365px) {
  .clinic-integration-box {
    height:                     calc(100dvh - 358px);
    overflow-x:                 hidden;
    overflow-y:                 scroll;
  }
}
</style>

<script>
const PASSWORD_MASK = '********'

function defaultConfig() {
  return {
    host: '',
    port: 22,
    username: '',
    password: '',
    knownHosts: '',
    remoteDir: '/upload'
  }
}

function buildForm(source) {
  if (source && source.uuid) {
    return {
      uuid: source.uuid,
      type: source.type || 'sftp',
      name: source.name,
      enabled: source.enabled !== undefined ? source.enabled : true,
      config: Object.assign(defaultConfig(), source.config || {}),
    }
  }
  return {
    uuid: null,
    type: 'sftp',
    name: null,
    enabled: true,
    config: defaultConfig(),
  }
}

export default {
  name: 'ClinicIntegrationEdit',
  props: ['clinic_id', 'integration'],
  data: () => ({
    loading: false,
    alert: {
      message: null,
      class: null
    },
    typeOptions: [],
    form: buildForm(null),
  }),
  watch: {
    integration: {
      immediate: true,
      handler(value) {
        this.form = buildForm(value)
      }
    }
  },
  mounted() {
    this.typeOptions = [
      {value: 'sftp', label: this.$i18n.t('integration.typeSftp')},
    ]

    this.$nextTick(() => {
      if (this.$refs.name) this.$refs.name.focus()
    })
  },
  methods: {
    clearMaskedPassword() {
      if (this.form.config.password === PASSWORD_MASK) {
        this.form.config.password = ''
      }
    },
    doSave: async function() {
      this.alert.message = null

      if (!this.form.name) {
        this.$message.popup(this, 'integration.nameRequired', 'operationWarning', 'warn')
        return
      }

      let params = {
        clinicId: this.clinic_id,
        integration: this.form
      }

      this.loading = true
      NProgress.start()
      try {
        await this.$clinicService.saveIntegration(params)
        this.$message.popup(this, 'general.createdSuccess', 'operationSuccess', 'success')
        this.$emit('saved')
      } catch (err) {
        console.log(err)
        this.$message.popup(this, 'general.createdError', 'operationFail', 'error')
      }
      NProgress.done(true)
      this.loading = false
    },
    doTest: async function() {
      this.loading = true
      NProgress.start()
      try {
        const params = {
          clinicId: this.clinic_id,
          type: this.form.type,
          config: this.form.config
        }
        if (this.form.uuid) {
          params.uuid = this.form.uuid
        }
        await this.$clinicService.testIntegration(params)
        this.$message.popup(this, 'integration.testSuccess', 'operationSuccess', 'success')
      } catch (err) {
        console.log(err)
        const code = err && err.response && err.response.data && err.response.data.errorMessage
        const key = code ? `integration.testError.${code}` : null
        const message = key && this.$te(key) ? key : 'integration.testFail'
        this.$message.popup(this, message, 'operationFail', 'error')
      }
      NProgress.done(true)
      this.loading = false
    },
    async confirmRemoval() {
      const ok = await this.$refs.offcanvasGeneric.show({
        title: this.$i18n.t('confirm'),
        text: this.$i18n.t('general.removeConfirm'),
        okButton: this.$i18n.t('remove'),
        okButtonClass: 'btn-danger',
      })
      if (ok) {
        this.doRemove()
      }
    },
    doRemove: async function() {
      this.loading = true
      NProgress.start()
      try {
        await this.$clinicService.removeIntegration({clinicId: this.clinic_id, uuid: this.form.uuid})
        this.$message.popup(this, 'integration.removeSuccess', 'operationSuccess', 'success')
        this.$emit('saved')
      } catch (err) {
        console.log(err)
        this.$message.popup(this, 'integration.removeError', 'operationFail', 'error')
      }
      NProgress.done(true)
      this.loading = false
    },
  },
}
</script>
